import { DefaultError, Mutation, MutationKey } from '@tanstack/query-core'
import { useMutationState } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'

export interface MutationStateTrackerOptions<
  TVariables = unknown,
  TData = unknown,
  TContext = unknown,
> {
  /**
   * The mutation key to track
   */
  mutationKey: MutationKey

  /**
   * Filter mutations to only those with specific status
   * @default undefined (all statuses)
   */
  status?: 'idle' | 'pending' | 'error' | 'success'

  /**
   * Only track mutations submitted after component mount
   * @default true
   */
  onlyRecentMutations?: boolean

  /**
   * Custom predicate function to filter mutations
   */
  predicate?: (
    mutation: Mutation<TData, DefaultError, TVariables, TContext>,
  ) => boolean
}

export interface TrackedMutation<TVariables> {
  status: 'idle' | 'pending' | 'error' | 'success'
  variables: TVariables | undefined
  submittedAt: number | undefined
}

/**
 * Hook to track mutation states with optional filtering by recency and status
 */
export function useMutationStateTracker<
  TVariables = unknown,
  TData = unknown,
  TContext = unknown,
>({
  mutationKey,
  status,
  onlyRecentMutations = true,
  predicate,
}: MutationStateTrackerOptions<TVariables, TData, TContext>) {
  const lastOpenedAt = useRef<number | null>(null)

  useEffect(() => {
    lastOpenedAt.current = Date.now()
  }, [])

  const rawMutations = useMutationState<TrackedMutation<TVariables>>({
    filters: {
      mutationKey,
      predicate: (mutation) => {
        // Apply recency filter if enabled
        if (onlyRecentMutations && lastOpenedAt.current !== null) {
          const isRecent = mutation.state.submittedAt >= lastOpenedAt.current
          if (!isRecent) return false
        }

        // Apply custom predicate if provided
        return (
          !predicate ||
          predicate(
            mutation as Mutation<TData, DefaultError, TVariables, TContext>,
          )
        )
      },
    },
    select: (mutation) => ({
      status: mutation.state.status,
      variables: mutation.state.variables as TVariables | undefined,
      submittedAt: mutation.state.submittedAt,
    }),
  })

  // Memoize filtered and sorted mutations to prevent unnecessary re-renders
  const mutations = useMemo(() => {
    const filteredMutations = status
      ? rawMutations.filter((mutation) => mutation.status === status)
      : rawMutations

    return filteredMutations.sort((a, b) => {
      if (a.submittedAt === undefined) return 1
      if (b.submittedAt === undefined) return -1
      return b.submittedAt - a.submittedAt
    })
  }, [rawMutations, status])

  // Memoize getMutationsFor function to provide stable reference
  const getMutationsFor = useCallback(
    (variables: Partial<TVariables>) => {
      return mutations.filter((mutation) => {
        return Object.entries(variables).every(([propertyName, value]) => {
          const key = propertyName as keyof TVariables
          return mutation.variables && mutation.variables[key] === value
        })
      })
    },
    [mutations],
  )

  return {
    /**
     * All tracked mutations, ordered by most recently submitted (nulls last)
     */
    mutations,
    /**
     * Get mutations for a specific variable property value
     * Useful for filtering by specific IDs or other properties
     * Now memoized to prevent infinite re-renders
     */
    getMutationsFor,
  }
}
